import { useEffect, useState, useCallback, useMemo, createContext, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const MINUTE_IN_MS = 60 * 1000;

// Setup axios to include token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  return String(detail);
};

// ==================== AUTH CONTEXT ====================
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('access_token', res.data.access_token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(false);
  };

  const value = useMemo(() => ({ user, loading, login, logout, checkAuth }), [user, loading, checkAuth]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ==================== PROTECTED ROUTE ====================
export const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><img src="/eliteiv-logo.png" alt="EliteIV" className="w-24 h-24 object-contain animate-pulse" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

// ==================== LOGIN PAGE ====================
export const getInitials = (firstName, lastName) => `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

// ==================== STAFF MANAGEMENT ====================
export const calcAge = (dob) => {
  if (!dob) return null;
  try {
    const birth = new Date(dob);
    const diff = Date.now() - birth.getTime();
    const age = new Date(diff).getUTCFullYear() - 1970;
    return Number.isFinite(age) && age >= 0 && age < 130 ? age : null;
  } catch { return null; }
};

export const formatPhone = (raw) => {
  if (!raw) return '';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return raw;
};

export const InfoField = ({ label, value, danger }) => (
  <div className="space-y-0.5">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{label}</p>
    <p className={`text-sm ${danger && value ? 'text-destructive font-medium' : 'text-foreground'}`}>{value || <span className="text-muted-foreground/50">—</span>}</p>
  </div>
);

export const DELIVERY_FEE_AMOUNT = 20.0;

export const fmtTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (h === 0 && m === 0) return '12:00am';
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')}${period}`;
};

// Hourly slots for Day View: 7 AM – 11 PM (covers 8AM–12AM operating hours)
export const fmtHour12 = (h) => {
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12} ${period}`;
};
